import React from 'react';
import { i18n } from 'i18n';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import Box from '@mui/material/Box';
import { Trans } from '@lingui/react';
import { throughputUnitLabelFromThroughputControlType } from 'hooks/useDisplayUnits/display';
import {
  BasicDisplayUnits,
  additionalUnitLabels,
  getPressurePumpUnitLabel,
  unitLabels
} from 'hooks/useDisplayUnits/useDisplayUnits';
import { round } from 'utils';
import {
  DisplayUnits,
  MetricPressureUnit,
  ModelNodeDto,
  PipeFrictionDataDto,
  PourDto,
  PourNoteRoutePoints,
  ThroughputControlType,
  UnitSystem,
  UcsCoefficientsDto,
  CustomUnitFields,
  PumpType
} from 'providers/api';
import {
  frictionLossMetricToImperial,
  isCustomUnitField,
  pumpPressureBarToGravityMetricHead,
  pumpPressureBarToKpa,
  pumpPressureKpaToBar,
  speedMetricToImperial
} from 'utils/unitConversions';
import ProjectContext from 'modules/project/providers/project/context';
import UnitSystemDetail, { UnitSystemValue } from './ProjectAreaPage/ProjectStopesPage/StopePanelPage/RecipesTab/UnitSystemDetail';
import { RoutePoint } from './ProjectAreaPage/ProjectStopesPage/MineModel/types';

interface BoosterPumpPressure {
  nodeId: string;
  pressure: number;
  boosterPressureLimit: number;
}

const getBoosterPumpPressures = (
  pour: PourDto,
  nodes: Pick<ModelNodeDto, 'nodeId' | 'boosterPressure'>[] | undefined,
  routePoints?: RoutePoint[],
): BoosterPumpPressure[] => {
  if (!nodes) return [];

  const points = (routePoints ?? pour.pourNote?.routePoints ?? []) as Array<RoutePoint | PourNoteRoutePoints>;
  if (points.length === 0) return [];

  const nodeMap = new Map(nodes.map((n) => [n.nodeId, n]));

  return points
    .filter((rp) => {
      let pointId = '';
      if ('pointId' in rp) {
        pointId = rp.pointId ?? '';
      } else if (rp.point && 'id' in rp.point) {
        pointId = rp.point.id;
      }
      if (!pointId) return false;
      const node = nodeMap.get(pointId);
      return node && node.boosterPressure && node.boosterPressure > 0;
    })
    .map((rp) => {
      let pointId = '';
      if ('pointId' in rp) {
        pointId = rp.pointId ?? '';
      } else if (rp.point && 'id' in rp.point) {
        pointId = rp.point.id;
      }
      const node = nodeMap.get(pointId)!;

      const pressure = 'pointId' in rp
        ? (rp.pressure ?? 0) / 100
        : pumpPressureKpaToBar(rp.pressure ?? 0);

      return {
        nodeId: pointId,
        pressure,
        boosterPressureLimit: node.boosterPressure!,
      };
    });
};

interface PourPasteFillDetailsProps {
  pour: PourDto;
  unitSystemPreference: UnitSystem;
  throughputControlType: ThroughputControlType;
  targetDaysCoefficients: { [key: string]: UcsCoefficientsDto; }
  customUnitFields: CustomUnitFields[];
  pumpType: PumpType;
  mineModelNodes?: Pick<ModelNodeDto, 'nodeId' | 'boosterPressure'>[];
  routePoints?: RoutePoint[];
  pipeFrictionDataItems?: PipeFrictionDataDto[];
  displayUnitPreferences?: DisplayUnits[];
  metricPressureUnit?: MetricPressureUnit;
}

const PourPasteFillDetails = ({
  pour,
  unitSystemPreference,
  throughputControlType,
  targetDaysCoefficients,
  customUnitFields,
  pumpType,
  mineModelNodes,
  routePoints,
  pipeFrictionDataItems,
  displayUnitPreferences,
  metricPressureUnit: metricPressureUnitProp,
}: PourPasteFillDetailsProps) => {
  const [pipeTableOpen, setPipeTableOpen] = React.useState(false);
  const boosterPumpPressures = getBoosterPumpPressures(pour, mineModelNodes, routePoints);
  const showPipeFrictionTable = displayUnitPreferences?.includes(DisplayUnits.FrictionLoss)
    && pipeFrictionDataItems
    && pipeFrictionDataItems.length > 0;
  const useImperialFriction = isCustomUnitField(unitSystemPreference, customUnitFields, additionalUnitLabels[DisplayUnits.FrictionLoss].customUnitField);
  const useImperialVelocity = isCustomUnitField(unitSystemPreference, customUnitFields, unitLabels[BasicDisplayUnits.Speed].customUnitField);
  const frictionUnit = useImperialFriction ? 'psi/ft' : 'kPa/m';
  const velocityUnit = useImperialVelocity ? 'ft/s' : 'm/s';
  const projectContext = React.useContext(ProjectContext);
  const metricPressureUnit = metricPressureUnitProp ?? projectContext?.state.metricPressureUnit ?? MetricPressureUnit.Bar;
  const isKiloPascal = metricPressureUnit === MetricPressureUnit.KiloPascal;
  const pressureDecimalPlaces = pumpType === PumpType.Pump && isKiloPascal ? 0 : 1;
  const toDisplayPumpOrGravity = (value: number | undefined) => {
    if (value === undefined) {
      return value;
    }
    if (pumpType === PumpType.Pump) {
      return unitSystemPreference === UnitSystem.Metric && isKiloPascal ? pumpPressureBarToKpa(value) : value;
    }
    return pumpPressureBarToGravityMetricHead(value);
  };
  const pressureLabel = pumpType === PumpType.Pump ? 'Pump Pressure' : 'Gravity';
  const pressureUnitInfo = pumpType === PumpType.Pump ? getPressurePumpUnitLabel(metricPressureUnit) : unitLabels[BasicDisplayUnits.Gravity];

  const targetDays = pour.pasteSpecification?.targetDays ?? (Object.keys(targetDaysCoefficients ?? {})[0] as unknown as number);
  const targetDaysLabel = `${targetDays} ${i18n._('days')}`;

  return (
    <>
      <UnitSystemDetail
        selected={pour.volume}
        unitSystem={unitSystemPreference}
        unitInfo={unitLabels[BasicDisplayUnits.Volume]}
        decimalPlaces={2}
        customUnitFields={customUnitFields}
      />
      <UnitSystemDetail
        designed={pour.pasteSpecification?.throughput}
        selected={pour.pasteSpecification?.throughput}
        unitSystem={unitSystemPreference}
        unitInfo={throughputUnitLabelFromThroughputControlType(throughputControlType)}
        decimalPlaces={2}
        customUnitFields={customUnitFields}
      />
      <UnitSystemDetail
        designed={toDisplayPumpOrGravity(pour.pasteSpecification?.pumpPressure)}
        selected={toDisplayPumpOrGravity(pour.pasteSelectedSpecification?.pumpPressure)}
        unitSystem={unitSystemPreference}
        unitInfo={pressureUnitInfo}
        decimalPlaces={pressureDecimalPlaces}
        customUnitFields={customUnitFields}
      />
      <UnitSystemDetail
        designed={pour.pasteSpecification?.targetUCSStrength}
        selected={pour.pasteSelectedSpecification?.ucsStrength}
        unitSystem={unitSystemPreference}
        unitInfo={unitLabels[BasicDisplayUnits.PressureUCS]}
        decimalPlaces={2}
        customUnitFields={customUnitFields}
      />

      <TableRow>
        <TableCell component="th" scope="row"><Trans id="Days">Days</Trans></TableCell>
        <TableCell align="right" width="auto">{targetDaysLabel}</TableCell>
        <TableCell align="right">{targetDaysLabel}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell component="th" scope="row"><Trans id="Concentration">Concentration</Trans></TableCell>
        <TableCell align="right" width="auto">{`${pour.pasteOptimisedSpecification?.massConcentration} %m`}</TableCell>
        <TableCell align="right">{`${pour.pasteSelectedSpecification?.massConcentration} %m`}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell component="th" scope="row"><Trans id="Binder">Binder</Trans></TableCell>
        <TableCell align="right" width="auto">{`${pour.pasteOptimisedSpecification?.binderContent} %`}</TableCell>
        <TableCell align="right">{`${pour.pasteSelectedSpecification?.binderContent} %`}</TableCell>
      </TableRow>

      {boosterPumpPressures.length > 0 && (
      <>
        <TableRow>
          <TableCell colSpan={3} sx={{ p: 0 }}>
            <Divider sx={{ my: 1 }} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={3} sx={{ py: 0.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              <Trans id="Booster Pump Pressure">Booster Pump Pressure</Trans>
            </Typography>
          </TableCell>
        </TableRow>
        {boosterPumpPressures.map((bp) => (
          <TableRow key={bp.nodeId}>
            <TableCell component="th" scope="row">{bp.nodeId}</TableCell>
            <TableCell align="right">
              <UnitSystemValue
                value={toDisplayPumpOrGravity(bp.pressure)}
                unitSystem={unitSystemPreference}
                unitInfo={pressureUnitInfo}
                decimalPlaces={pressureDecimalPlaces}
                customUnitFields={customUnitFields}
              />
            </TableCell>
            <TableCell align="right">
              <UnitSystemValue
                value={toDisplayPumpOrGravity(bp.boosterPressureLimit)}
                unitSystem={unitSystemPreference}
                unitInfo={pressureUnitInfo}
                decimalPlaces={pressureDecimalPlaces}
                customUnitFields={customUnitFields}
              />
            </TableCell>
          </TableRow>
        ))}
      </>
    )}

      {showPipeFrictionTable && (
      <>
        <TableRow>
          <TableCell colSpan={3} sx={{ p: 0 }}>
            <Divider sx={{ my: 1 }} />
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={3} sx={{ py: 0.5 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => setPipeTableOpen(!pipeTableOpen)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                <Trans id="Pipe Friction & Velocity">Pipe Friction & Velocity</Trans>
              </Typography>
              <IconButton size="small">
                {pipeTableOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={3} sx={{ p: 0 }}>
            <Collapse in={pipeTableOpen}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><Trans id="Pipe">Pipe</Trans></TableCell>
                    <TableCell align="right"><Trans id="Friction Loss">Friction Loss</Trans></TableCell>
                    <TableCell align="right"><Trans id="Velocity">Velocity</Trans></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pipeFrictionDataItems!.map((pipe) => {
                    const friction = useImperialFriction ? frictionLossMetricToImperial(pipe.frictionLoss ?? 0) : pipe.frictionLoss ?? 0;
                    const velocity = useImperialVelocity ? speedMetricToImperial(pipe.velocity ?? 0) : pipe.velocity ?? 0;
                    return (
                      <TableRow key={pipe.pipeId}>
                        <TableCell component="th" scope="row">{pipe.pipeName}</TableCell>
                        <TableCell align="right">{`${round(friction, 1)} ${frictionUnit}`}</TableCell>
                        <TableCell align="right">{`${round(velocity, 2)} ${velocityUnit}`}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    )}

      {(pour.pasteSlackFlowSpecification || pour.pasteMaxPressureSpecification) && (
      <>
        <TableRow>
          <TableCell colSpan={3} sx={{ p: 0 }}>
            <Divider sx={{ my: 1 }} />
          </TableCell>
        </TableRow>

        {pour.pasteSlackFlowSpecification && (
          <>
            <TableRow>
              <TableCell colSpan={3} sx={{ py: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  <Trans id="Slack Flow">Slack Flow</Trans>
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row"><Trans id={pressureLabel}>{pressureLabel}</Trans></TableCell>
              <TableCell align="right" colSpan={2}>
                <UnitSystemValue
                  value={toDisplayPumpOrGravity(pour.pasteSlackFlowSpecification.pumpPressure)}
                  unitSystem={unitSystemPreference}
                  unitInfo={pressureUnitInfo}
                  decimalPlaces={pressureDecimalPlaces}
                  customUnitFields={customUnitFields}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row"><Trans id="Mass Conc.">Mass Conc.</Trans></TableCell>
              <TableCell align="right" colSpan={2}>
                {`${round(pour.pasteSlackFlowSpecification.massConcentration, 2)} %`}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row"><Trans id="Binder">Binder</Trans></TableCell>
              <TableCell align="right" colSpan={2}>
                {`${round(pour.pasteSlackFlowSpecification.binderContent, 2)} %`}
              </TableCell>
            </TableRow>
          </>
        )}

        {pour.pasteMaxPressureSpecification && (
          <>
            <TableRow>
              <TableCell colSpan={3} sx={{ py: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  <Trans id="Max Pressure">Max Pressure</Trans>
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row"><Trans id={pressureLabel}>{pressureLabel}</Trans></TableCell>
              <TableCell align="right" colSpan={2}>
                <UnitSystemValue
                  value={toDisplayPumpOrGravity(pour.pasteMaxPressureSpecification.pumpPressure)}
                  unitSystem={unitSystemPreference}
                  unitInfo={pressureUnitInfo}
                  decimalPlaces={pressureDecimalPlaces}
                  customUnitFields={customUnitFields}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row"><Trans id="Mass Conc.">Mass Conc.</Trans></TableCell>
              <TableCell align="right" colSpan={2}>
                {`${round(pour.pasteMaxPressureSpecification.massConcentration, 2)} %`}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row"><Trans id="Binder">Binder</Trans></TableCell>
              <TableCell align="right" colSpan={2}>
                {`${round(pour.pasteMaxPressureSpecification.binderContent, 2)} %`}
              </TableCell>
            </TableRow>
          </>
        )}
      </>
    )}
    </>
  );
};

export default PourPasteFillDetails;
